import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Eye, EyeOff, Pencil, Trash2, ExternalLink, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CmsActualites({ navigate }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', section: 'restaurant', published: true });

  const load = () => fetch(`${API}/api/site/actualites/all`).then(r => r.json()).then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ title: '', content: '', section: 'restaurant', published: true });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.title) return;
    if (editing) {
      await fetch(`${API}/api/site/actualites/${editing}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
    } else {
      await fetch(`${API}/api/site/actualites`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
    }
    resetForm();
    load();
  };

  const handleEdit = (item) => {
    setForm({ title: item.title, content: item.content, section: item.section || 'restaurant', published: item.published });
    setEditing(item.id);
    setShowForm(true);
  };

  const togglePublished = async (item) => {
    await fetch(`${API}/api/site/actualites/${item.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ published: !item.published })
    });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette actualite ?')) return;
    await fetch(`${API}/api/site/actualites/${id}`, { method: 'DELETE' });
    load();
  };

  return (
    <div data-testid="cms-actualites">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('dashboard')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm" data-testid="actualites-back-btn">
            <ArrowLeft size={16} /> Retour
          </button>
          <div className="flex items-center gap-2">
            <span className="text-gray-300">|</span>
            <h1 className="text-lg font-bold text-gray-900">Actualites</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/site/restaurant" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <ExternalLink size={14} /> Voir le site
          </a>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600"
            data-testid="new-actualite-btn">
            <Plus size={16} /> Nouvelle actualite
          </button>
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 space-y-4" data-testid="actualite-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">{editing ? 'Modifier' : 'Nouvelle actualite'}</h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
          <input type="text" placeholder="Titre" value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" data-testid="actualite-title-input" />
          <textarea placeholder="Contenu de l'actualite..." rows={4} value={form.content}
            onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none" data-testid="actualite-content-input" />
          <div className="flex items-center gap-3">
            <button onClick={handleSave} className="bg-[#1a2744] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#243559]" data-testid="save-actualite-btn">
              {editing ? 'Enregistrer' : 'Publier'}
            </button>
            <button onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700">Annuler</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {items.map(item => (
          <div key={item.id}
            className={`bg-white border rounded-lg p-5 ${!item.published ? 'border-gray-200 opacity-60' : 'border-gray-200'}`}
            data-testid={`actualite-${item.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{item.content}</p>
                <p className="text-xs text-gray-400 mt-2">
                  Publie le {new Date(item.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => togglePublished(item)}
                  className="p-2 rounded-lg hover:bg-gray-100" title={item.published ? 'Masquer' : 'Publier'}
                  data-testid={`toggle-actualite-${item.id}`}>
                  {item.published ? <Eye size={18} className="text-green-500" /> : <EyeOff size={18} className="text-gray-400" />}
                </button>
                <button onClick={() => handleEdit(item)} className="p-2 rounded-lg hover:bg-gray-100" title="Modifier" data-testid={`edit-actualite-${item.id}`}>
                  <Pencil size={18} className="text-blue-500" />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-2 rounded-lg hover:bg-red-50" title="Supprimer" data-testid={`delete-actualite-${item.id}`}>
                  <Trash2 size={18} className="text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-sm text-gray-400 py-12">Aucune actualite</p>}
      </div>
    </div>
  );
}
